import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { services } from '../data/mock';
import { Button } from '../components/ui/button';
import { ArrowRight, Globe, Search, Handshake, Truck, FileCheck, TrendingUp, CheckCircle2, MapPin } from 'lucide-react';
import { SEO } from '../components/SEO';

const iconMap = {
  Globe: Globe,
  Search: Search,
  Handshake: Handshake,
  Truck: Truck,
  FileCheck: FileCheck,
  TrendingUp: TrendingUp,
  MapPin: MapPin,
};

export default function ServicesPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Trade Services"
        path="/services"
        description="Comprehensive import and export services including logistics coordination, international sourcing, buyer-supplier matching, and documentation compliance."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-navy-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-teal-500/20 text-teal-400 text-sm font-medium mb-6">
                Our Services
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Comprehensive
                <span className="block text-teal-400">Import & Export Solutions</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                From sourcing to delivery, we provide end-to-end import and export solutions 
                that simplify global trade for Australian businesses.
              </p>
            </div>
          </div>
        </section>

        {/* Services Detail */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-20">
              {services.map((service, index) => {
                const Icon = iconMap[service.icon];
                const isEven = index % 2 === 0;
                return (
                  <div
                    key={service.id}
                    id={service.id}
                    className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:grid-flow-dense' : ''}`}
                  >
                    <div className={!isEven ? 'lg:col-start-2' : ''}>
                      <div className="p-4 w-fit rounded-2xl bg-navy-900 mb-6">
                        <Icon className="h-10 w-10 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-navy-900 mb-4">
                        {service.title}
                      </h2>
                      <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                        {service.description}
                      </p>
                      <div className="space-y-3 mb-8">
                        {service.features.map((feature) => (
                          <div key={feature} className="flex items-center gap-3">
                            <CheckCircle2 className="h-5 w-5 text-teal-600 flex-shrink-0" />
                            <span className="text-slate-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                      <Link to="/contact">
                        <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                          Get Started
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                    <div className={`rounded-3xl overflow-hidden ${isEven ? '' : 'lg:col-start-1'}`}>
                      <div className="aspect-[4/3] w-full">
                        <img 
                          src={service.image} 
                          alt={service.title}
                          className="w-full h-full object-cover rounded-3xl shadow-lg"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-navy-900 mb-4">How We Work</h2>
              <p className="text-xl text-slate-600">A streamlined process designed for efficiency and results</p>
            </div>
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: '01', title: 'Consultation', desc: 'Understand your import or export requirements' },
                { step: '02', title: 'Planning', desc: 'Develop tailored strategy and timeline' },
                { step: '03', title: 'Execution', desc: 'Handle sourcing, compliance, and logistics' },
                { step: '04', title: 'Delivery', desc: 'Ensure successful delivery and support' },
              ].map((item, index) => (
                <div key={item.step} className="relative">
                  {index < 3 && (
                    <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-teal-200" />
                  )}
                  <div className="relative bg-white p-6 rounded-2xl border border-slate-200 text-center">
                    <div className="w-16 h-16 mx-auto rounded-full bg-navy-900 text-white flex items-center justify-center text-2xl font-bold mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-lg font-semibold text-navy-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Premium Services CTA */}
        <section className="py-16 bg-gradient-to-br from-teal-600 to-teal-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Need Expert Assistance?
            </h2>
            <p className="text-lg text-teal-100 mb-8">
              Book our premium services for personalised consultations, market research reports, 
              supplier verification, and more.
            </p>
            <Link to="/pricing">
              <Button className="bg-white text-teal-700 hover:bg-teal-50 px-8 py-4 rounded-xl text-lg font-semibold">
                View Premium Services & Pricing
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
